import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import Close from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useAppointmentsCalendarContext } from 'components/appointment_calendar_context';
import dayjs from 'dayjs';
import Localization from 'l18n/l18n';
import Appointments from 'models/appointments';
import CalendarSettings from 'models/calendar';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

const StyledFormControlLabel = styled((props) => (
  <FormControlLabel {...props} sx={{ fontSize: '12rem !important' }} />
))(({ theme, checked }) => ({
  '.MuiFormControlLabel-label': {
    fontSize: '.8rem !important',
    margin: 0,
    color: checked ? '#279BFF' : 'inherit',
  },
}));
const ChackBoxStyles = styled('span')(({ theme }) => ({
  borderRadius: 5,
  width: 20,
  height: 20,
  border: '1px solid #757575',
  backgroundColor: theme.palette.mode === 'dark' ? '#394b59' : '#f5f8fa',
  '.Mui-focusVisible &': {
    outline: '2px auto rgba(19,124,189,.6)',
    outlineOffset: 1,
  },
}));

const ChackBoxCheckedStyles = styled(ChackBoxStyles)({
  backgroundColor: 'white',
  borderColor: '#279BFF',
  '&:before': {
    display: 'block',
    width: 20,
    height: 20,
    borderRadius: 5,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23279BFF'/%3E%3C/svg%3E\")",
    content: '""',
  },
});
const StyledTextField = styled(TextField)({
  border: '0 !important',
  borderRadius: 0,
  background: '#eeeeee',
  width: '100%',

  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: 0, // - Set the Input border
    },
  },
});
function MyFormControlLabel(props) {
  return (
    <StyledFormControlLabel checked={props.control.props.checked} {...props} />
  );
}

const AvailableTimeDialog = React.forwardRef((props, ref) => {
  const { handleCancel } = props;
  const { calendarSettings } = useAppointmentsCalendarContext();
  const [weekends, setWeekends] = React.useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, control } = useForm({
    defaultValues: {
      start_date: dayjs(),
      end_date: dayjs(),
      start_time: dayjs(),
      end_time: dayjs(),
      time_period: '15',
    },
  });

  // handle form submit when clickin on save
  const onSubmit = (data) => {
    let { start_time, end_time, start_date, end_date, time_period } = data;
    const formatedData = {
      start_time: dayjs(start_time).second(0).format('HH:mm:ss'),
      end_time: dayjs(end_time).second(0).format('HH:mm:ss'),
      start_date: dayjs(start_date).format('YYYY-MM-DD'),
      end_date: dayjs(end_date).format('YYYY-MM-DD'),
      time_period,
    };

    // save weekends
    CalendarSettings.changeWeekends({ week_ends: weekends })
      .then((result) => {

        // generate appointment calendar
        Appointments.generateCalendar({ ...formatedData })
          .then((result) => {
            enqueueSnackbar(Localization.translate['generate_calendar_success'], {
              variant: 'success',
              anchorOrigin: { vertical: 'top', horizontal: 'center' },
              preventDuplicate: true,
              autoHideDuration: 2000,
            });
          })
          .catch((err) => {
            console.log({ ...formatedData });
          });
      })
      .catch((err) => {
        enqueueSnackbar(Localization.translate['error'], {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          preventDuplicate: true,
          autoHideDuration: 2000,
        });
        console.log('error', err);
      });
  };
  const handleCheckboxChange = React.useCallback((event) => {
    const weekDays = weekends;
    // check if the user trying to execude all days
    if (
      weekDays.filter((day) => day.executed == true).length >=
        weekDays.length - 1 &&
      event.target.checked == true
    ) {
      enqueueSnackbar(Localization.translate['can_not_execute_all_days'], {
        variant: 'warning',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        preventDuplicate: true,
        autoHideDuration: 2000,
      });
      return;
    }

    weekDays.map((day) => {
      // if the changed input name match one of the weekDays id
      if (day.id == Number(event.target.name)) {
        day.executed = event.target.checked;
      }
    });

    setWeekends((previous) => {
      return [...previous];
    });
  });

  // set action for the save button from the forwarded ref
  React.useImperativeHandle(ref, () => ({
    save() {
      handleSubmit(onSubmit)();
    },
  }));

  React.useEffect(() => {
    // get weekends on dialog opens
    CalendarSettings.getWeekends()
      .then((res) => {
        const weekDays = res.week_ends;
        // convert the result to the same schema as the weekDays state in the calendar settings state inside the appointment context
        let namedWeekDays = Object.entries(weekDays).map((day) => {
          return {
            id: day[0],
            executed: day[1],
            name: Localization.translate['week_days'][day[0]],
          };
        });

        setWeekends(
          weekDays[0] !== undefined
            ? [...weekends, ...namedWeekDays]
            : calendarSettings.weekDays
        );
      })
      .catch((err) => {
        console.log(err);
      });
  }, [CalendarSettings, calendarSettings]);

  const timePeriodGenerator = () => {
    let times = [];
    for (let i = 15; i < 125; ) {
      var hours = Math.floor(i / 60);
      var minutes = Math.round((i / 60 - hours) * 60);
      let timeName = '';
      if (hours > 0) {
        timeName +=
          hours == 1 ? 'ساعة' : hours == 2 ? 'ساعتين' : `${hours} ساعات`;
        if (minutes > 0) {
          timeName += ' و ';
        }
      }
      if (minutes > 0) {
        timeName += minutes == 2 ? 'دقيقتان' : `${minutes} دقيقة`;
      }
      times.push({ value: i, name: timeName });

      i = i >= 60 ? i + 15 : i + 5;
    }
    return times;
  };
  const timePeriod = timePeriodGenerator();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DialogTitle>
        <Grid container alignItems="center">
          <Grid item xs={11} className="draggable-dialog-title">
            {Localization.translate['manage_available_time']}
          </Grid>
          <Grid item xs={1}>
            <IconButton onClick={() => handleCancel()}>
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 6 }} component="form" noValidate autoComplete="off">
          <Grid container spacing={2}>
            <Grid container spacing={2} justify="space-between">
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="p">
                    {Localization.translate['start_work_day']}
                  </Typography>
                  <Controller
                    name={'start_date'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        onChange={onChange}
                        value={value}
                        sx={{ width: '100%' }}
                        components={{
                          OpenPickerIcon: CalendarTodayOutlined,
                        }}
                        OpenPickerButtonProps={{ style: { color: '#279BFF' } }}
                        renderInput={(params) => (
                          <StyledTextField dir="ltr" {...params} />
                        )}
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="p">
                    {Localization.translate['end_work_day']}
                  </Typography>
                  <Controller
                    name={'end_date'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        onChange={onChange}
                        value={value}
                        sx={{ width: '100%' }}
                        components={{
                          OpenPickerIcon: CalendarTodayOutlined,
                        }}
                        OpenPickerButtonProps={{ style: { color: '#279BFF' } }}
                        renderInput={(params) => (
                          <StyledTextField dir="ltr" {...params} />
                        )}
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="p">
                    {Localization.translate['start_work_hour']}
                  </Typography>
                  <Controller
                    name={'start_time'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TimePicker
                        select
                        onChange={onChange}
                        value={value}
                        OpenPickerButtonProps={{ style: { color: '#279BFF' } }}
                        sx={{ width: '100%' }}
                        renderInput={(params) => (
                          <StyledTextField dir="ltr" {...params} />
                        )}
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="p">
                    {Localization.translate['end_work_hour']}
                  </Typography>
                  <Controller
                    name={'end_time'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <TimePicker
                        select
                        onChange={onChange}
                        value={value}
                        OpenPickerButtonProps={{ style: { color: '#279BFF' } }}
                        sx={{ width: '100%' }}
                        renderInput={(params) => (
                          <StyledTextField dir="ltr" {...params} />
                        )}
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <Typography variant="p">
                    {Localization.translate['time_period']}
                  </Typography>
                  <Controller
                    name={'time_period'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <StyledTextField
                        select
                        onChange={onChange}
                        value={value}
                        sx={{ width: '100%' }}
                      >
                        {timePeriod.map((time) => {
                          return (
                            <MenuItem
                              dir="rtl"
                              key={time.value}
                              value={time.value}
                            >
                              {` ${time.name}`}
                            </MenuItem>
                          );
                        })}
                      </StyledTextField>
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Typography variant="p">
                    {Localization.translate['vications']}
                  </Typography>
                  <FormGroup sx={{ display: 'flex', flexDirection: 'row' }}>
                    {weekends.map((day) => {
                      return (
                        <MyFormControlLabel
                          key={day.id}
                          control={
                            <Checkbox
                              sx={{ padding: 0.5 }}
                              checked={day.executed}
                              checkedIcon={<ChackBoxCheckedStyles />}
                              icon={<ChackBoxStyles />}
                              onChange={handleCheckboxChange}
                              name={String(day.id)}
                            />
                          }
                          label={day.name}
                        />
                      );
                    })}
                  </FormGroup>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </LocalizationProvider>
  );
});
AvailableTimeDialog.displayName = 'AvailableTimeDialog';
export default AvailableTimeDialog;
